// Default share-card configuration. Single source of truth used by both
// /preview/share-card (public) and the admin configurator.

export const SC_DEFAULT_CONFIG = {
  accent: "#2e6c2e",
  domain: "ontour.scootive.pl",
  hashtag: "scootiveontour",
  // Content toggles
  showCampaignTitle: false,
  showCampaignDescription: false,
  showWinnerDescription: true,
  showWinnerRegion: true,
  showStats: true,
  showTop: true,
  showHashtag: true,
  showEndsAt: true, // pokazuj datę zakończenia głosowania (statyczna, bez timera)
  // Sizes (S / M / L / XL)
  winnerSize: "L",
  campaignTitleSize: "S",
  topLabel: "auto", // "auto" → use campaign.results_title, or custom string
};

export const SIZE_MAP = {
  story: {
    winner: { S: 38, M: 48, L: 60, XL: 78 },
    campaign: { S: 14, M: 18, L: 22, XL: 28 },
  },
  post: {
    winner: { S: 32, M: 40, L: 52, XL: 64 },
    campaign: { S: 12, M: 15, L: 18, XL: 22 },
  },
};

// Bundled templates
export const SC_TEMPLATES = [
  {
    id: "classic-green",
    name: "Classic Green (Scootive)",
    config: { ...SC_DEFAULT_CONFIG },
  },
  {
    id: "minimal-black",
    name: "Minimal Black",
    config: {
      ...SC_DEFAULT_CONFIG,
      accent: "#0e1410",
      showStats: false,
      showHashtag: false,
      winnerSize: "XL",
    },
  },
  {
    id: "campaign-focus",
    name: "Campaign Focus",
    config: {
      ...SC_DEFAULT_CONFIG,
      showCampaignTitle: true,
      showCampaignDescription: true,
      winnerSize: "M",
      campaignTitleSize: "L",
    },
  },
  {
    id: "results-only",
    name: "Tylko wyniki + Top 5",
    config: {
      ...SC_DEFAULT_CONFIG,
      showCampaignTitle: false,
      showWinnerDescription: false,
      showWinnerRegion: false,
      showStats: false,
      winnerSize: "L",
    },
  },
];

export function loadTemplates() {
  const raw = localStorage.getItem("scootive_sharecard_templates");
  if (!raw) return [];
  try {
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

export function saveTemplate(name, config) {
  const existing = loadTemplates();
  const id = `tpl_${Date.now()}`;
  const next = [...existing, { id, name, config, custom: true }];
  localStorage.setItem("scootive_sharecard_templates", JSON.stringify(next));
  return next;
}

export function deleteTemplate(id) {
  const existing = loadTemplates();
  const next = existing.filter((t) => t.id !== id);
  localStorage.setItem("scootive_sharecard_templates", JSON.stringify(next));
  return next;
}

// Title that adapts to campaign's results_title (singular or full).
export function topListLabel(config, campaign, count = 5) {
  if (config.topLabel && config.topLabel !== "auto") return config.topLabel;
  const rt = (campaign?.results_title || "").trim();
  if (!rt) return `Top ${count}`;
  // If campaign already has "Top" prefix, use it as-is. Otherwise prepend Top.
  if (/^top\b/i.test(rt)) return rt;
  return `Top ${count} · ${rt}`;
}

// Static "until" date label for sharecards (no countdown, just date+time).
// Always rendered in Europe/Warsaw timezone — that's what the campaign owner
// and Polish viewers expect, regardless of where the renderer (Chrome on the
// server) happens to live.
// Returns e.g. "Głosowanie do 31.08.2026 · 23:59"
export function formatEndsAt(endsAt) {
  if (!endsAt) return "";
  const d = new Date(endsAt);
  if (Number.isNaN(d.getTime())) return "";
  const fmt = new Intl.DateTimeFormat("pl-PL", {
    timeZone: "Europe/Warsaw",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  // Intl formatter returns "23.05.2026, 10:35" — split for our custom separator
  const parts = fmt.formatToParts(d);
  const get = (t) => parts.find((p) => p.type === t)?.value || "";
  const date = `${get("day")}.${get("month")}.${get("year")}`;
  const time = `${get("hour")}:${get("minute")}`;
  return `Głosowanie do ${date} · ${time}`;
}

// Returns `{ days, perDay }` describing how long the campaign has been live
// (from created_at — or starts_at, when set — up to ends_at or now, whichever
// is earlier). Used in the Story stats box subtitle.
export function campaignActivitySummary(campaign, totalVotes) {
  if (!campaign) return { days: 0, perDay: 0 };
  const start = new Date(campaign.starts_at || campaign.created_at || Date.now());
  const ends = new Date(campaign.ends_at || Date.now());
  const cutoff = Math.min(ends.getTime(), Date.now());
  const diffMs = cutoff - start.getTime();
  const days = Math.max(1, Math.round(diffMs / 86_400_000));
  const perDay = totalVotes && days > 0 ? Math.round(totalVotes / days) : 0;
  return { days, perDay };
}
